// Leagues — player progression badges keyed off user.wins.
//
// Tier thresholds (lower bound inclusive, upper bound exclusive):
//   Bronze  : 0  – 19 wins
//   Iron    : 20 – 39 wins
//   Silver  : 40 – 59 wins
//   Diamond : 60 – 99 wins
//   Ruby    : 100+ wins
//
// 0-win users land in Bronze (the "starter" league) rather than an
// awkward Unranked state. Crossing a threshold immediately bumps the
// badge — no separate promotion event yet, just a recompute on render.

import { getUser } from './views.js';
import { fetchLeaderboard, effectiveWinsFor } from './leaderboard.js';
import { ownerBadgeHTML } from './ownerBadge.js';

// Order matters — first-match-wins logic walks this top-down so each
// tier's `min` is the minimum win count to qualify for that tier.
// `max` is exclusive (next-tier-min - 1). Ruby has no ceiling.
export const LEAGUE_TIERS = [
    { key: 'bronze',  name: 'Bronze',  min: 0,   next: 20,  color: '#a06a3e', glow: 'rgba(186, 113, 49, 0.45)' },
    { key: 'iron',    name: 'Iron',    min: 20,  next: 40,  color: '#9fa6b2', glow: 'rgba(170, 178, 190, 0.45)' },
    { key: 'silver',  name: 'Silver',  min: 40,  next: 60,  color: '#d8d8e0', glow: 'rgba(220, 224, 235, 0.55)' },
    { key: 'diamond', name: 'Diamond', min: 60,  next: 100, color: '#6dd5ff', glow: 'rgba(109, 213, 255, 0.6)' },
    { key: 'ruby',    name: 'Ruby',    min: 100, next: null, color: '#ff5260', glow: 'rgba(255, 82, 96, 0.6)' },
];

// Returns the LEAGUE_TIERS entry matching the supplied win count.
// Wins below 0 (defensive) clamp to Bronze.
export function leagueForWins(wins) {
    const w = Math.max(0, Number(wins) || 0);
    let best = LEAGUE_TIERS[0];
    for (const t of LEAGUE_TIERS) {
        if (w >= t.min) best = t;
        else break;
    }
    return best;
}

// Returns { currentLeague, nextLeague, winsIntoTier, winsNeeded, progressPct }.
// nextLeague is null when the user is at Ruby (no further tier).
export function leagueProgress(wins) {
    const w = Math.max(0, Number(wins) || 0);
    const current = leagueForWins(w);
    const idx = LEAGUE_TIERS.indexOf(current);
    const next = (idx >= 0 && idx < LEAGUE_TIERS.length - 1) ? LEAGUE_TIERS[idx + 1] : null;
    if (!next) {
        return { currentLeague: current, nextLeague: null, winsIntoTier: w - current.min, winsNeeded: 0, progressPct: 100 };
    }
    const span = next.min - current.min;
    const intoTier = Math.max(0, w - current.min);
    const pct = Math.max(0, Math.min(100, Math.round((intoTier / span) * 100)));
    return { currentLeague: current, nextLeague: next, winsIntoTier: intoTier, winsNeeded: next.min - w, progressPct: pct };
}

function renderCurrentBlock(progress, wins) {
    const { currentLeague, nextLeague, progressPct, winsNeeded } = progress;
    const next = nextLeague
        ? `<span class="leagues-next-hint">${winsNeeded} win${winsNeeded === 1 ? '' : 's'} until <span class="league-${nextLeague.key}">${nextLeague.name}</span></span>`
        : '<span class="leagues-next-hint">Top tier — keep racking up wins!</span>';
    return `
        <div class="leagues-current-row">
            <span class="league-badge league-badge-lg league-${currentLeague.key}">${currentLeague.name}</span>
            <div class="leagues-current-info">
                <div class="leagues-wins-line"><strong>${wins}</strong> win${wins === 1 ? '' : 's'}</div>
                ${next}
            </div>
        </div>
        <div class="leagues-progress-bar"><div class="leagues-progress-fill" style="width:${progressPct}%; background:${currentLeague.color};"></div></div>
    `;
}

function renderTierList(currentKey) {
    return LEAGUE_TIERS.map(t => {
        const range = t.next === null ? `${t.min}+ wins` : `${t.min}–${t.next - 1} wins`;
        const isCurrent = (t.key === currentKey);
        return `
            <li class="leagues-tier-row${isCurrent ? ' leagues-tier-current' : ''}">
                <span class="league-badge league-${t.key}">${t.name}</span>
                <span class="leagues-tier-range">${range}</span>
            </li>
        `;
    }).join('');
}

// Per-user board inside the leagues modal: shows everyone returned by
// the leaderboard API, sorted by wins descending, with a league badge
// next to each name. Loaded asynchronously after the modal opens so a
// slow backend doesn't block the modal from rendering.
async function renderLeaguesBoard() {
    const boardEl = document.getElementById('leaguesBoardList');
    if (!boardEl) return;
    boardEl.innerHTML = '<li class="leagues-board-loading">Loading…</li>';
    let rows = [];
    try {
        const data = await fetchLeaderboard();
        rows = Array.isArray(data) ? data : [];
    } catch (err) {
        console.error('[leagues] fetchLeaderboard failed:', err);
        boardEl.innerHTML = '<li class="leagues-board-loading">Couldn\'t load board.</li>';
        return;
    }
    // Sort by effective wins descending (real wins when present,
    // score-based proxy otherwise); secondary by raw score descending;
    // tertiary by username so order is stable when both tie.
    rows.sort((a, b) => {
        const aw = effectiveWinsFor(a), bw = effectiveWinsFor(b);
        if (bw !== aw) return bw - aw;
        const as = Number(a.score) || 0, bs = Number(b.score) || 0;
        if (bs !== as) return bs - as;
        return String(a.username || '').localeCompare(String(b.username || ''));
    });
    if (rows.length === 0) {
        boardEl.innerHTML = '<li class="leagues-board-loading">No players yet.</li>';
        return;
    }
    boardEl.innerHTML = rows.map((u, i) => {
        const wins = effectiveWinsFor(u);
        const tier = leagueForWins(wins);
        return `
            <li class="leagues-board-row">
                <span class="leagues-board-rank">#${i + 1}</span>
                <span class="leagues-board-name">${u.username || 'Unknown'}${ownerBadgeHTML(u.username)}</span>
                <span class="league-badge league-${tier.key}">${tier.name}</span>
                <span class="leagues-board-wins">${wins} win${wins === 1 ? '' : 's'}</span>
            </li>
        `;
    }).join('');
}

export function showLeaguesModal() {
    const modal = document.getElementById('leaguesModal');
    if (!modal) return;
    const user = getUser() || {};
    const wins = Math.max(0, Number(user.wins) || 0);
    const progress = leagueProgress(wins);

    const currentBlock = document.getElementById('leaguesCurrentBlock');
    if (currentBlock) currentBlock.innerHTML = renderCurrentBlock(progress, wins);

    const list = document.getElementById('leaguesList');
    if (list) list.innerHTML = renderTierList(progress.currentLeague.key);

    modal.style.display = 'flex';
    // Kick off the per-user board fetch; the modal is already visible
    // so the user sees the tier UI immediately and the board fills in.
    renderLeaguesBoard();
}

export function hideLeaguesModal() {
    const modal = document.getElementById('leaguesModal');
    if (modal) modal.style.display = 'none';
}

let isWired = false;
export function initLeaguesUI() {
    if (isWired) return;
    isWired = true;
    const btn = document.getElementById('leaguesButton');
    if (btn) btn.addEventListener('click', showLeaguesModal);
    const close = document.getElementById('leaguesCloseButton');
    if (close) close.addEventListener('click', hideLeaguesModal);
    // Click on backdrop closes the modal.
    const modal = document.getElementById('leaguesModal');
    if (modal) {
        modal.addEventListener('click', (e) => {
            if (e.target === modal) hideLeaguesModal();
        });
    }
}
